import { useRef, useState } from 'react';

interface CoverImageFieldProps {
  /** URL to preview (a newly-selected object URL or an existing server image). */
  previewUrl: string | null;
  onSelect: (file: File) => void;
  /** When provided, shows a "Remover" action (use only when a new file is selected). */
  onRemove?: () => void;
  accept?: string;
  /** `cover` crops to the 16:9 frame (article covers); `contain` shows the whole
   *  image letterboxed (general images of unknown ratio). */
  fit?: 'cover' | 'contain';
}

const chip =
  'bg-on-surface/70 hover:bg-on-surface/85 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm transition-colors';

// Wide 16:9 cover-image field: click-or-drag dropzone when empty; full preview
// at the published ratio with Alterar/Remover overlays when filled.
export function CoverImageField({
  previewUrl,
  onSelect,
  onRemove,
  accept = 'image/jpeg,image/png,image/webp',
  fit = 'cover',
}: CoverImageFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function take(files: FileList | null) {
    const file = files?.[0];
    if (file) onSelect(file);
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => take(e.target.files)}
      />
      {previewUrl ? (
        <div className="border-line bg-background relative aspect-video w-full overflow-hidden rounded-lg border">
          <img
            src={previewUrl}
            alt="Pré-visualização da imagem"
            className={`h-full w-full ${fit === 'contain' ? 'object-contain' : 'object-cover'}`}
          />
          <div className="absolute right-2 bottom-2 flex gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} className={chip}>
              <span className="material-symbols-outlined text-[14px]">edit</span>
              Alterar
            </button>
            {onRemove && (
              <button type="button" onClick={onRemove} className={chip}>
                <span className="material-symbols-outlined text-[14px]">delete</span>
                Remover
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            take(e.dataTransfer.files);
          }}
          className={`flex aspect-video w-full flex-col items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed text-sm transition-colors ${
            dragging
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-line-strong text-on-surface-light hover:border-primary'
          }`}
        >
          <span className="material-symbols-outlined text-primary text-3xl">cloud_upload</span>
          Arraste uma imagem ou clique para escolher
          <span className="text-on-surface-light/70 text-xs">JPG, PNG ou WebP</span>
        </button>
      )}
    </>
  );
}
